import React, { useState, useEffect, useRef } from "react";
import { collection, getDocs, deleteDoc, doc, setDoc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { GAME_ID, formatMoney } from "../utils/helpers";
import { styles } from "../styles/theme";

function AdminPage({ onLogout }) {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [logs, setLogs] = useState([]);
  const [minigameStats, setMinigameStats] = useState({});
  const [gameData, setGameData] = useState(null);
  const [players, setPlayers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(null);
  const [iniciarEnMinutos, setIniciarEnMinutos] = useState(10); // Tiempo por defecto para iniciar el juego
  const previousRoundRef = useRef(null);

  // Suscripción al estado del juego
  useEffect(() => {
    const gameRef = doc(db, "games", GAME_ID);
    const unsub = onSnapshot(gameRef, (snap) => {
      if (snap.exists()) {
        const newData = snap.data();
        
        // Detectar reinicio del juego (ronda bajó a 1 desde una ronda mayor)
        if (previousRoundRef.current !== null && 
            previousRoundRef.current > 1 && 
            newData.currentRound === 1) {
          // El juego se reinició, refrescar la página
          window.location.reload();
          return;
        }
        
        previousRoundRef.current = newData.currentRound;
        setGameData(newData);
      }
    });
    return () => unsub();
  }, []);

  // Suscripción a los jugadores
  useEffect(() => {
    const playersCol = collection(db, "games", GAME_ID, "players");
    const unsub = onSnapshot(playersCol, (snap) => {
      const playersList = [];
      snap.forEach((doc) => {
        playersList.push({ id: doc.id, ...doc.data() });
      });
      // Ordenar por panes descendente, luego por dinero
      playersList.sort((a, b) => {
        const breadsA = a.breads || 0;
        const breadsB = b.breads || 0;
        if (breadsB !== breadsA) return breadsB - breadsA;
        const moneyA = a.money || 0;
        const moneyB = b.money || 0;
        return moneyB - moneyA;
      });
      setPlayers(playersList);
    });
    return () => unsub();
  }, []);

  // Calcular tiempo restante
  useEffect(() => {
    if (!gameData) {
      setTimeLeft(null);
      return;
    }

    // Si está pausado, no actualizar el contador
    if (gameData.isPaused) {
      return;
    }

    if (gameData.roundStatus === "open" && gameData.roundEndTime) {
      const endMs = gameData.roundEndTime.toMillis
        ? gameData.roundEndTime.toMillis()
        : gameData.roundEndTime;
      
      const interval = setInterval(() => {
        // Verificar nuevamente dentro del interval
        if (gameData.isPaused) {
          return;
        }
        const diff = Math.max(0, Math.round((endMs - Date.now()) / 1000));
        setTimeLeft(diff);
      }, 1000);
      return () => clearInterval(interval);
    }

    if (gameData.roundStatus === "waiting" && gameData.gameStart) {
      const startMs = gameData.gameStart.toMillis
        ? gameData.gameStart.toMillis()
        : gameData.gameStart;
      
      const interval = setInterval(() => {
        // Verificar nuevamente dentro del interval
        if (gameData.isPaused) {
          return;
        }
        const diff = Math.max(0, Math.round((startMs - Date.now()) / 1000));
        setTimeLeft(diff);
      }, 1000);
      return () => clearInterval(interval);
    }

    setTimeLeft(null);
  }, [gameData]);

  // Cargar estadísticas
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const historialCol = collection(db, "games", GAME_ID, "historial");
      const snapshot = await getDocs(historialCol);
      
      const allLogs = [];
      snapshot.forEach((doc) => {
        allLogs.push({ id: doc.id, ...doc.data() });
      });

      // Ordenar por timestamp
      allLogs.sort((a, b) => {
        const timeA = a.timestamp?.toMillis ? a.timestamp.toMillis() : 0;
        const timeB = b.timestamp?.toMillis ? b.timestamp.toMillis() : 0;
        return timeB - timeA;
      });

      setLogs(allLogs);

      // Calcular estadísticas de minijuegos
      const stats = {};
      allLogs.forEach((log) => {
        if (log.type === "minigame_reward") {
          const source = log.data?.source || "unknown";
          if (!stats[source]) {
            stats[source] = {
              count: 0,
              totalMoney: 0,
              players: new Set(),
            };
          }
          stats[source].count++;
          stats[source].totalMoney += log.data?.amount || 0;
          stats[source].players.add(log.data?.playerId);
        }
      });

      // Convertir Set a count
      Object.keys(stats).forEach((key) => {
        stats[key].uniquePlayers = stats[key].players.size;
        delete stats[key].players;
      });

      setMinigameStats(stats);
    } catch (error) {
      console.error("Error al cargar estadísticas:", error);
      setMessage("Error al cargar estadísticas");
    }
  };

  const handleDownloadLogs = () => {
    try {
      // Convertir logs a texto
      let logText = "=== LOGS DEL JUEGO DE INFLACIÓN ===\n";
      logText += `Fecha de descarga: ${new Date().toLocaleString('es-ES')}\n`;
      logText += `Total de logs: ${logs.length}\n`;
      logText += "=".repeat(50) + "\n\n";
      
      logs.forEach((log, index) => {
        const timestamp = log.timestamp?.toDate
          ? log.timestamp.toDate().toLocaleString('es-ES')
          : "Sin fecha";
        
        logText += `[${index + 1}] ${timestamp}\n`;
        logText += `Tipo: ${log.type}\n`;
        logText += `Mensaje: ${log.message}\n`;
        
        if (log.data && Object.keys(log.data).length > 0) {
          logText += "Datos:\n";
          Object.entries(log.data).forEach(([key, value]) => {
            logText += `  - ${key}: ${JSON.stringify(value)}\n`;
          });
        }
        
        logText += "-".repeat(50) + "\n\n";
      });
      
      // Crear blob y descargar
      const blob = new Blob([logText], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `logs_inflacion_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setMessage("✅ Logs descargados correctamente");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error al descargar logs:", error);
      setMessage("❌ Error al descargar logs: " + error.message);
    }
  };

  const handleResetGame = async () => {
    if (!window.confirm("¿Estás seguro de que quieres reiniciar COMPLETAMENTE el juego? Esto borrará todos los jugadores, ofertas, datos del juego y logs.")) {
      return;
    }

    setLoading(true);
    setMessage("Reiniciando juego...");

    try {
      // 1. Borrar todos los jugadores
      const playersCol = collection(db, "games", GAME_ID, "players");
      const playersSnap = await getDocs(playersCol);
      for (const playerDoc of playersSnap.docs) {
        await deleteDoc(playerDoc.ref);
      }

      // 2. Borrar todas las rondas (ofertas y votos)
      for (let round = 1; round <= 10; round++) {
        // Borrar ofertas
        const bidsCol = collection(db, "games", GAME_ID, "rounds", String(round), "bids");
        const bidsSnap = await getDocs(bidsCol);
        for (const bidDoc of bidsSnap.docs) {
          await deleteDoc(bidDoc.ref);
        }

        // Borrar votos
        const votesCol = collection(db, "games", GAME_ID, "rounds", String(round), "pollVotes");
        const votesSnap = await getDocs(votesCol);
        for (const voteDoc of votesSnap.docs) {
          await deleteDoc(voteDoc.ref);
        }
      }

      // 3. Borrar todos los logs del historial
      const historialCol = collection(db, "games", GAME_ID, "historial");
      const historialSnap = await getDocs(historialCol);
      for (const logDoc of historialSnap.docs) {
        await deleteDoc(logDoc.ref);
      }

      // 4. Resetear el documento del juego
      const gameRef = doc(db, "games", GAME_ID);
      await setDoc(gameRef, {
        currentRound: 1,
        totalRounds: 6,
        roundStatus: "waiting",
        gameStart: new Date(Date.now() + iniciarEnMinutos * 60000), // Inicia en X minutos
        currentPollOption: null,
        baseStipend: 10, // Ayuda base por defecto
        stipendMultiplier: 1, // Multiplicador inicial
        lastProcessedRound: 0, // Para evitar procesamiento duplicado
        lastProcessedPoll: 0, // Para evitar procesamiento duplicado de encuestas
        lastStipendRound: 0, // Para evitar distribución duplicada de ayuda
        gameStarted: false, // Permitir que se unan nuevos jugadores
      });

      setMessage("✅ Juego reiniciado correctamente. Todos los datos han sido borrados.");
      
      // Recargar estadísticas
      setTimeout(() => {
        loadStats();
      }, 1000);
    } catch (error) {
      console.error("Error al reiniciar juego:", error);
      setMessage("❌ Error al reiniciar el juego: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLogs = async () => {
    if (!window.confirm("¿Estás seguro de que quieres borrar todos los logs y estadísticas? Esta acción no se puede deshacer.")) {
      return;
    }

    setLoading(true);
    setMessage("Borrando logs y estadísticas...");

    try {
      const historialCol = collection(db, "games", GAME_ID, "historial");
      const snapshot = await getDocs(historialCol);
      
      for (const logDoc of snapshot.docs) {
        await deleteDoc(logDoc.ref);
      }

      setLogs([]);
      setMinigameStats({});
      setMessage("✅ Logs y estadísticas borrados exitosamente.");
    } catch (error) {
      console.error("Error al borrar logs:", error);
      setMessage("❌ Error al borrar los logs: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePause = async () => {
    const isPaused = gameData?.isPaused || false;
    const action = isPaused ? "reanudar" : "pausar";
    
    if (!window.confirm(`¿Estás seguro de que quieres ${action} el juego?`)) {
      return;
    }

    setLoading(true);
    setMessage(`${isPaused ? "Reanudando" : "Pausando"} el juego...`);

    try {
      const gameRef = doc(db, "games", GAME_ID);
      
      if (isPaused) {
        // Reanudar: ajustar los tiempos según el tiempo pausado
        const pauseDuration = Date.now() - (gameData.pausedAt || Date.now());
        const updates = { isPaused: false, pausedAt: null };
        
        if (gameData.roundEndTime) {
          const currentEndTime = gameData.roundEndTime.toMillis ? gameData.roundEndTime.toMillis() : gameData.roundEndTime;
          updates.roundEndTime = new Date(currentEndTime + pauseDuration);
        }
        
        if (gameData.gameStart) {
          const currentStartTime = gameData.gameStart.toMillis ? gameData.gameStart.toMillis() : gameData.gameStart;
          updates.gameStart = new Date(currentStartTime + pauseDuration);
        }
        
        await setDoc(gameRef, updates, { merge: true });
        setMessage("✅ Juego reanudado exitosamente.");
      } else {
        // Pausar
        await setDoc(gameRef, {
          isPaused: true,
          pausedAt: Date.now(),
        }, { merge: true });
        setMessage("⏸️ Juego pausado exitosamente.");
      }
    } catch (error) {
      console.error("Error al pausar/reanudar juego:", error);
      setMessage("❌ Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={{ ...styles.card, maxWidth: 800 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h1 style={styles.title}>Panel de Administrador</h1>
          <button style={adminStyles.logoutButton} onClick={onLogout}>
            Cerrar Sesión
          </button>
        </div>

        {/* Estado del juego */}
        {gameData && (
          <section style={{ marginBottom: 24 }}>
            <h3 style={styles.sectionTitle}>🎮 Estado del Juego</h3>
            <div style={adminStyles.gameInfoGrid}>
              <div style={adminStyles.infoCard}>
                <span style={adminStyles.infoLabel}>Ronda actual:</span>
                <span style={adminStyles.infoValue}>
                  {gameData.currentRound}
                </span>
              </div>
              <div style={adminStyles.infoCard}>
                <span style={adminStyles.infoLabel}>Estado:</span>
                <span style={adminStyles.infoValue}>
                  {gameData.isPaused ? "⏸️ Pausado" :
                   gameData.roundStatus === "open" ? "🟢 Jugando" : 
                   gameData.roundStatus === "waiting" ? "🟡 Esperando" : 
                   "🔴 Finalizado"}
                </span>
              </div>
              <div style={adminStyles.infoCard}>
                <span style={adminStyles.infoLabel}>Tiempo restante:</span>
                <span style={adminStyles.infoValue}>
                  {timeLeft !== null ? (
                    gameData.roundStatus === "open" ? 
                      `${Math.floor(timeLeft / 60)}:${String(timeLeft % 60).padStart(2, '0')}` :
                      `${timeLeft}s`
                  ) : "N/A"}
                </span>
              </div>
              <div style={adminStyles.infoCard}>
                <span style={adminStyles.infoLabel}>Jugadores:</span>
                <span style={adminStyles.infoValue}>{players.length}</span>
              </div>
            </div>
          </section>
        )}

        {/* Lista de jugadores */}
        <section style={{ marginBottom: 24 }}>
          <h3 style={styles.sectionTitle}>👥 Jugadores ({players.length})</h3>
          {players.length === 0 ? (
            <p style={adminStyles.noData}>No hay jugadores registrados</p>
          ) : (
            <div style={adminStyles.playersContainer}>
              {players.map((player, index) => (
                <div key={player.id} style={adminStyles.playerCard}>
                  <div style={adminStyles.playerRank}>#{index + 1}</div>
                  <div style={adminStyles.playerInfo}>
                    <div style={adminStyles.playerName}>{player.name}</div>
                    <div style={adminStyles.playerStats}>
                      <span>🍞 {player.breads || 0} panes</span>
                      <span style={{ marginLeft: 12 }}>
                        💰 {formatMoney(player.money || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Botón de reinicio */}
        <section style={{ marginBottom: 24 }}>
          <h3 style={styles.sectionTitle}>🔄 Control del Juego</h3>
          
          {/* Campo para elegir minutos de espera */}
          <div style={{ marginBottom: 16 }}>
            <label style={adminStyles.inputLabel}>
              ⏱️ Tiempo de espera para iniciar (minutos):
              <input
                type="number"
                min="0"
                max="60"
                value={iniciarEnMinutos}
                onChange={(e) => setIniciarEnMinutos(Math.max(0, parseInt(e.target.value) || 0))}
                style={adminStyles.numberInput}
              />
            </label>
            <p style={adminStyles.inputHint}>
              El juego comenzará {iniciarEnMinutos} minuto(s) después de reiniciar
            </p>
          </div>
          
          <div style={{ display: "flex", gap: 12, flexDirection: "column" }}>
            <button
              style={adminStyles.resetButton}
              onClick={handleResetGame}
              disabled={loading}
            >
              {loading ? "Reiniciando..." : "Reiniciar Juego Completamente"}
            </button>
            <button
              style={gameData?.isPaused ? adminStyles.resumeButton : adminStyles.pauseButton}
              onClick={handleTogglePause}
              disabled={loading || !gameData || gameData.roundStatus === "finished"}
            >
              {gameData?.isPaused ? "▶️ Reanudar Juego" : "⏸️ Pausar Juego"}
            </button>
            <button
              style={adminStyles.downloadButton}
              onClick={handleDownloadLogs}
              disabled={logs.length === 0}
            >
              📥 Descargar Logs ({logs.length})
            </button>
            <button
              style={adminStyles.deleteLogsButton}
              onClick={handleDeleteLogs}
              disabled={loading || logs.length === 0}
            >
              🗑️ Borrar Logs ({logs.length})
            </button>
          </div>
          {message && (
            <p style={adminStyles.message}>{message}</p>
          )}
        </section>

        {/* Estadísticas de minijuegos */}
        <section style={{ marginBottom: 24 }}>
          <h3 style={styles.sectionTitle}>📊 Estadísticas de Minijuegos</h3>
          {Object.keys(minigameStats).length === 0 ? (
            <p style={adminStyles.noData}>No hay datos de minijuegos aún</p>
          ) : (
            <div style={adminStyles.statsGrid}>
              {Object.entries(minigameStats).map(([game, stats]) => (
                <div key={game} style={adminStyles.statCard}>
                  <h4 style={adminStyles.statTitle}>{game}</h4>
                  <div style={adminStyles.statRow}>
                    <span style={adminStyles.statLabel}>Veces jugado:</span>
                    <span style={adminStyles.statValue}>{stats.count}</span>
                  </div>
                  <div style={adminStyles.statRow}>
                    <span style={adminStyles.statLabel}>Dinero ganado:</span>
                    <span style={adminStyles.statValue}>${stats.totalMoney}</span>
                  </div>
                  <div style={adminStyles.statRow}>
                    <span style={adminStyles.statLabel}>Jugadores únicos:</span>
                    <span style={adminStyles.statValue}>{stats.uniquePlayers}</span>
                  </div>
                  <div style={adminStyles.statRow}>
                    <span style={adminStyles.statLabel}>Promedio por juego:</span>
                    <span style={adminStyles.statValue}>
                      ${(stats.totalMoney / stats.count).toFixed(1)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Gráfica visual de interacciones */}
        <section style={{ marginBottom: 24 }}>
          <h3 style={styles.sectionTitle}>📈 Gráfica de Interacciones</h3>
          <div style={adminStyles.chartContainer}>
            {Object.entries(minigameStats).map(([game, stats]) => {
              const maxCount = Math.max(...Object.values(minigameStats).map(s => s.count));
              const percentage = maxCount > 0 ? (stats.count / maxCount) * 100 : 0;
              
              return (
                <div key={game} style={adminStyles.barContainer}>
                  <div style={adminStyles.barLabel}>{game}</div>
                  <div style={adminStyles.barWrapper}>
                    <div
                      style={{
                        ...adminStyles.bar,
                        width: `${percentage}%`,
                      }}
                    />
                    <span style={adminStyles.barCount}>{stats.count}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Logs recientes */}
        <section>
          <h3 style={styles.sectionTitle}>📝 Últimos 20 Logs</h3>
          <div style={adminStyles.logsContainer}>
            {logs.slice(0, 20).map((log) => {
              const timestamp = log.timestamp?.toDate
                ? log.timestamp.toDate().toLocaleString('es-ES')
                : "Sin fecha";
              
              return (
                <div key={log.id} style={adminStyles.logItem}>
                  <div style={adminStyles.logHeader}>
                    <span style={adminStyles.logType}>{log.type}</span>
                    <span style={adminStyles.logTime}>{timestamp}</span>
                  </div>
                  <div style={adminStyles.logMessage}>{log.message}</div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

const adminStyles = {
  logoutButton: {
    background: "rgba(255, 80, 80, 0.3)",
    border: "1px solid #ff7f7f55",
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 12,
    color: "#ffcccc",
    cursor: "pointer",
  },
  gameInfoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 12,
    marginTop: 12,
  },
  infoCard: {
    background: "rgba(0, 40, 80, 0.6)",
    border: "1px solid rgba(0, 155, 255, 0.3)",
    borderRadius: 8,
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  infoLabel: {
    fontSize: 11,
    color: "#A8D5FF",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  infoValue: {
    fontSize: 16,
    color: "#E8F6FF",
    fontWeight: 700,
  },
  playersContainer: {
    marginTop: 12,
    maxHeight: 400,
    overflowY: "auto",
  },
  playerCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "rgba(0, 40, 80, 0.4)",
    border: "1px solid rgba(0, 155, 255, 0.2)",
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  playerRank: {
    fontSize: 18,
    fontWeight: 700,
    color: "#5DB6FF",
    minWidth: 40,
    textAlign: "center",
  },
  playerInfo: {
    flex: 1,
  },
  playerName: {
    fontSize: 14,
    fontWeight: 600,
    color: "#E8F6FF",
    marginBottom: 4,
  },
  playerStats: {
    fontSize: 12,
    color: "#A8D5FF",
  },
  resetButton: {
    width: "100%",
    background: "linear-gradient(135deg, #ff4444, #cc0000)",
    borderRadius: 8,
    border: "none",
    padding: "12px",
    fontSize: 14,
    fontWeight: 600,
    color: "#fff",
    cursor: "pointer",
  },
  forceReloadButton: {
    width: "100%",
    background: "linear-gradient(135deg, #00cc88, #00aa66)",
    borderRadius: 8,
    border: "none",
    padding: "12px",
    fontSize: 14,
    fontWeight: 600,
    color: "#fff",
    cursor: "pointer",
    transition: "opacity 0.3s ease",
  },
  pauseButton: {
    width: "100%",
    background: "linear-gradient(135deg, #ffaa00, #ff8800)",
    borderRadius: 8,
    border: "none",
    padding: "12px",
    fontSize: 14,
    fontWeight: 600,
    color: "#fff",
    cursor: "pointer",
    transition: "opacity 0.3s ease",
  },
  resumeButton: {
    width: "100%",
    background: "linear-gradient(135deg, #00cc44, #00aa33)",
    borderRadius: 8,
    border: "none",
    padding: "12px",
    fontSize: 14,
    fontWeight: 600,
    color: "#fff",
    cursor: "pointer",
    transition: "opacity 0.3s ease",
  },
  downloadButton: {
    width: "100%",
    background: "linear-gradient(135deg, #0099FF, #0077CC)",
    borderRadius: 8,
    border: "none",
    padding: "12px",
    fontSize: 14,
    fontWeight: 600,
    color: "#fff",
    cursor: "pointer",
    transition: "opacity 0.3s ease",
  },
  deleteLogsButton: {
    width: "100%",
    background: "linear-gradient(135deg, #ff8800, #ff6600)",
    borderRadius: 8,
    border: "none",
    padding: "12px",
    fontSize: 14,
    fontWeight: 600,
    color: "#fff",
    cursor: "pointer",
    transition: "opacity 0.3s ease",
  },
  message: {
    fontSize: 13,
    padding: 8,
    borderRadius: 6,
    background: "rgba(0, 155, 255, 0.1)",
    border: "1px solid rgba(0, 155, 255, 0.3)",
    color: "#5DB6FF",
    marginTop: 8,
  },
  noData: {
    fontSize: 13,
    color: "#A8D5FF",
    fontStyle: "italic",
  },
  inputLabel: {
    display: "block",
    fontSize: 14,
    color: "#A8D5FF",
    fontWeight: 600,
    marginBottom: 8,
  },
  numberInput: {
    display: "block",
    width: "120px",
    padding: "8px 12px",
    marginTop: 8,
    fontSize: 16,
    fontWeight: "bold",
    background: "rgba(0, 40, 80, 0.6)",
    border: "2px solid rgba(0, 155, 255, 0.5)",
    borderRadius: 8,
    color: "#E8F6FF",
    outline: "none",
    transition: "border-color 0.2s",
  },
  inputHint: {
    fontSize: 12,
    color: "#A8D5FF",
    marginTop: 6,
    fontStyle: "italic",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 12,
    marginTop: 12,
  },
  statCard: {
    background: "rgba(0, 40, 80, 0.6)",
    border: "1px solid rgba(0, 155, 255, 0.3)",
    borderRadius: 8,
    padding: 12,
  },
  statTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#5DB6FF",
    marginBottom: 8,
    textTransform: "capitalize",
  },
  statRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 4,
    fontSize: 12,
  },
  statLabel: {
    color: "#A8D5FF",
  },
  statValue: {
    color: "#E8F6FF",
    fontWeight: 600,
  },
  chartContainer: {
    marginTop: 12,
  },
  barContainer: {
    marginBottom: 12,
  },
  barLabel: {
    fontSize: 12,
    color: "#A8D5FF",
    marginBottom: 4,
    textTransform: "capitalize",
  },
  barWrapper: {
    position: "relative",
    background: "rgba(0, 40, 80, 0.6)",
    height: 28,
    borderRadius: 4,
    border: "1px solid rgba(0, 155, 255, 0.3)",
    overflow: "hidden",
  },
  bar: {
    position: "absolute",
    left: 0,
    top: 0,
    height: "100%",
    background: "linear-gradient(135deg, #0077CC, #0099FF)",
    transition: "width 0.3s ease",
  },
  barCount: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 12,
    color: "#E8F6FF",
    fontWeight: 600,
    zIndex: 1,
  },
  logsContainer: {
    marginTop: 12,
    maxHeight: 400,
    overflowY: "auto",
  },
  logItem: {
    background: "rgba(0, 40, 80, 0.4)",
    border: "1px solid rgba(0, 155, 255, 0.2)",
    borderRadius: 6,
    padding: 8,
    marginBottom: 8,
  },
  logHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  logType: {
    fontSize: 11,
    color: "#5DB6FF",
    fontWeight: 600,
    textTransform: "uppercase",
  },
  logTime: {
    fontSize: 10,
    color: "#A8D5FF",
  },
  logMessage: {
    fontSize: 12,
    color: "#E8F6FF",
  },
};

export default AdminPage;
